// Radiology dictation helpers shared by the web app, the Chrome extension
// and the API (plain code, no I/O):
// - matchStudyToTemplate: pick the dictation template for a study name read
//   from the reporting page ("Pelvic & Transrectal Prostate U/S"), by
//   modality and body part;
// - applySpokenCorrections: apply "scratch that", "change X to Y" and
//   "X, I mean Y" inside one dictation before the report is filled.

export type StudyTemplateChoice = {
  id: string;
  name: string;
  aliases?: readonly string[];
  // The template's report text; its heading and line labels count too.
  body?: string;
};

export type StudyTemplateMatch<T extends StudyTemplateChoice> = {
  template: T;
  score: number;
};

// Imaging modalities, in canonical form.
const MODALITIES = new Set(["us", "ct", "mr", "xr", "nm", "mammo", "fluoro"]);

const CANONICAL: Record<string, string> = {
  ultrasound: "us",
  ultrasonography: "us",
  sonogram: "us",
  sonography: "us",
  sono: "us",
  us: "us",
  echo: "us",
  ct: "ct",
  cat: "ct",
  tomography: "ct",
  mri: "mr",
  mr: "mr",
  xr: "xr",
  xray: "xr",
  radiograph: "xr",
  radiographs: "xr",
  film: "xr",
  films: "xr",
  nm: "nm",
  nuclear: "nm",
  mammogram: "mammo",
  mammography: "mammo",
  mammo: "mammo",
  fluoroscopy: "fluoro",
  abdomen: "abdo",
  abdominal: "abdo",
  abdo: "abdo",
  pelvis: "pelvic",
  pelvic: "pelvic",
  kidney: "renal",
  kidneys: "renal",
  renal: "renal",
  bladder: "bladder",
  urinary: "bladder",
  prostate: "prostate",
  prostatic: "prostate",
  transrectal: "transrectal",
  trus: "transrectal",
  endorectal: "transrectal",
  transvaginal: "transvaginal",
  endovaginal: "transvaginal",
  tv: "transvaginal",
  thyroid: "thyroid",
  neck: "neck",
  breast: "breast",
  breasts: "breast",
  liver: "liver",
  hepatic: "liver",
  gallbladder: "gallbladder",
  biliary: "gallbladder",
  ruq: "ruq",
  scrotal: "scrotal",
  scrotum: "scrotal",
  testicular: "scrotal",
  testes: "scrotal",
  testis: "scrotal",
  obstetric: "obstetric",
  obstetrical: "obstetric",
  ob: "obstetric",
  pregnancy: "obstetric",
  fetal: "obstetric",
  carotid: "carotid",
  carotids: "carotid",
  venous: "venous",
  dvt: "venous",
  arterial: "arterial",
  chest: "chest",
  thorax: "chest",
  thoracic: "chest",
  lung: "chest",
  lungs: "chest",
  head: "head",
  brain: "head",
  spine: "spine",
  lumbar: "spine",
  cervical: "spine",
  thoracolumbar: "spine",
  male: "male",
  female: "female",
  uterus: "uterus",
  ovaries: "ovary",
  ovary: "ovary",
  ovarian: "ovary",
  aorta: "aorta",
  aortic: "aorta",
  appendix: "appendix",
  soft: "soft",
  tissue: "tissue",
  hip: "hip",
  shoulder: "shoulder",
  knee: "knee",
};

// Words that say nothing about the study (or say it twice).
const IGNORED = new Set([
  "and",
  "of",
  "the",
  "a",
  "an",
  "with",
  "without",
  "w",
  "wo",
  "for",
  "to",
  "in",
  "on",
  "study",
  "exam",
  "examination",
  "scan",
  "imaging",
  "report",
  "template",
  "templates",
  "my",
  "use",
  "normal",
  "standard",
  "usual",
  "complete",
  "limited",
  "routine",
  "left",
  "right",
  "bilateral",
  "bilat",
  "lt",
  "rt",
  "only",
  "views",
  "view",
  "doppler",
  "color",
  "assessment",
  "findings",
  "interpretation",
  "impression",
  "technique",
  "clinical",
  "indication",
  "comparison",
  "date",
  "exams",
  "visibility",
  "clarity",
  "regions",
  "free",
  "fluid",
  "tree",
  "lower",
  "quadrants",
]);

/** Canonical study words ("U/S" -> us, "Kidneys" -> renal). */
export const studyTokens = (text: string): string[] => {
  const words = text
    .toLowerCase()
    .replace(/\bu\s*\/\s*s\b/g, " us ")
    .replace(/\bx[\s-]?rays?\b/g, " xr ")
    .replace(/\bk\.?\s?u\.?\s?b\b/g, " renal bladder ")
    .replace(/kidneys?,? ureters?,? (and )?bladder/g, " renal bladder ")
    .replace(/right upper quadrant/g, " ruq ")
    .replace(/[^a-z0-9]+/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  const out: string[] = [];
  for (const word of words) {
    if (IGNORED.has(word) || /^\d+$/.test(word) || word.length < 2) continue;
    const canonical = CANONICAL[word] ?? word;
    if (!out.includes(canonical)) out.push(canonical);
  }
  return out;
};

// Weighted words of a template: its name, spoken names and heading count
// fully; the labels of its report lines ("Prostate:") count less.
const templateWords = (template: StudyTemplateChoice): Map<string, number> => {
  const words = new Map<string, number>();
  const add = (text: string, weight: number): void => {
    for (const word of studyTokens(text))
      words.set(word, Math.max(words.get(word) ?? 0, weight));
  };
  add(template.name, 1);
  for (const alias of template.aliases ?? []) add(alias, 1);
  const lines = (template.body ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines[0]) add(lines[0].replace(/\[[^\]]*\]/g, " "), 1);
  for (const line of lines.slice(1)) {
    const label = line.replace(/^[-•*\d.)\s]+/, "").match(/^([^:[\]]{2,40}):/);
    if (label?.[1]) add(label[1], 0.6);
  }
  return words;
};

export const STUDY_MATCH_THRESHOLD = 0.4;

/**
 * The template that fits a study name best, or null when none is close
 * enough. Body-part words decide; a different modality (CT study, US
 * template) costs most of the score, the same modality adds a little.
 */
export function matchStudyToTemplate<T extends StudyTemplateChoice>(
  study: string,
  templates: readonly T[],
  threshold = STUDY_MATCH_THRESHOLD,
): StudyTemplateMatch<T> | null {
  const tokens = studyTokens(study);
  const modalities = tokens.filter((word) => MODALITIES.has(word));
  const parts = tokens.filter((word) => !MODALITIES.has(word));
  if (!parts.length && !modalities.length) return null;
  let best: StudyTemplateMatch<T> | null = null;
  let second = 0;
  for (const template of templates) {
    const words = templateWords(template);
    const own = [...words.keys()].filter((word) => MODALITIES.has(word));
    let score = parts.length
      ? parts.reduce((sum, word) => sum + (words.get(word) ?? 0), 0) /
        parts.length
      : 0.3;
    if (modalities.length && own.length) {
      score = modalities.some((word) => own.includes(word))
        ? score + 0.1
        : score * 0.3;
    }
    if (!best || score > best.score) {
      second = best?.score ?? 0;
      best = { template, score };
    } else if (score > second) second = score;
  }
  if (!best || best.score < threshold) return null;
  // Two templates fit equally well: let the radiologist choose.
  if (best.score - second < 0.05) return null;
  return { template: best.template, score: Math.round(best.score * 100) / 100 };
}

// ---------------------------------------------------------------------------
// Spoken corrections inside one dictation.

export type SpokenChange = { from: string; to: string };

export type SpokenCorrections = {
  // The dictation with the corrections applied (commands removed). A
  // "change X to Y" whose X is not in this dictation (it is in the report,
  // when dictating more) stays in the text for the fill to apply.
  text: string;
  // How many corrections were applied here.
  applied: number;
  // "change X to Y" commands left for the fill.
  pending: SpokenChange[];
  // `text` with each pending command replaced by its new value only, for
  // the value check: the old value is meant to leave the report.
  checkText: string;
};

const SCRATCH = /\b(?:scratch|strike|delete|erase|cancel)\s+that\b[\s.,;:!?]*/i;
const UNIT =
  "(?:cm|mm|cc|ml|mL|%|percent|centimet(?:er|re)s?|millimet(?:er|re)s?|milliliters?)";
const NUMBER = "\\d+(?:\\.\\d+)?";
const I_MEAN = new RegExp(
  `(${NUMBER})(\\s*${UNIT})?\\s*[,.;]?\\s*(?:(?:no|sorry|correction)[,.]?\\s+)?I\\s+mean[,]?\\s+(${NUMBER})(\\s*${UNIT})?`,
  "gi",
);
// A decimal point ("10.8") does not end a clause.
const CHANGE =
  /(^|[.!?;\n]\s*|,\s*)(?:(?:please|okay|ok|now)[,\s]+)?(?:change|correct|replace)\s+(?:the\s+)?((?:[^.!?;\n]|\.(?=\d)){1,60}?)\s+(?:to|with|should\s+be|into)\s+((?:[^.!?;\n,]|\.(?=\d)){1,60}?)\s*(?=[.!?;\n,](?!\d)|$)[.!?;]?/gi;

const words = (text: string): number =>
  text.split(/\s+/).filter(Boolean).length;

// Remove the last spoken unit before a "scratch that": the last sentence,
// or its last comma clause when the sentence is long.
const dropLastUnit = (before: string): string => {
  const trimmed = before.replace(/[\s,.;:!?-]+$/, "");
  const boundary = Math.max(
    trimmed.lastIndexOf(". "),
    trimmed.lastIndexOf("? "),
    trimmed.lastIndexOf("! "),
    trimmed.lastIndexOf("; "),
    trimmed.lastIndexOf("\n"),
  );
  const start = boundary < 0 ? 0 : boundary + 1;
  const sentence = trimmed.slice(start);
  const comma = sentence.lastIndexOf(",");
  if (comma > 0 && words(sentence) >= 10)
    return `${trimmed.slice(0, start + comma).trimEnd()},`;
  return trimmed.slice(0, start).trimEnd();
};

const joinParts = (before: string, after: string): string =>
  [before.trim(), after.trim()].filter(Boolean).join(" ");

const escapeRegExp = (text: string): string =>
  text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// The old words in the text before the command, as a whole-word match.
const findLast = (
  text: string,
  phrase: string,
): { index: number; length: number } | null => {
  const pattern = new RegExp(
    `(?<![\\w.])${escapeRegExp(phrase.trim()).replace(/\s+/g, "\\s+")}(?![\\w]|\\.\\d)`,
    "gi",
  );
  let found: { index: number; length: number } | null = null;
  for (const match of text.matchAll(pattern))
    found = { index: match.index ?? 0, length: match[0].length };
  return found;
};

const lastNumber = new RegExp(`${NUMBER}(?!.*\\d)`);

export function applySpokenCorrections(dictation: string): SpokenCorrections {
  let text = dictation;
  let applied = 0;

  // "40 cc, I mean 42 cc" -> "42 cc"
  text = text.replace(
    I_MEAN,
    (_all, _old: string, oldUnit: string | undefined, value: string, unit) => {
      applied++;
      return `${value}${unit ?? oldUnit ?? ""}`;
    },
  );

  // "... volume 40 cc. Scratch that. Volume 42 cc." -> "... Volume 42 cc."
  for (let guard = 0; guard < 50; guard++) {
    const match = SCRATCH.exec(text);
    if (!match) break;
    const before = text.slice(0, match.index);
    const after = text.slice(match.index + match[0].length);
    if (before.trim()) applied++;
    text = joinParts(dropLastUnit(before), after);
  }

  // "change left kidney 10.8 to 10.6" -> the earlier "10.8" becomes "10.6"
  const pending: SpokenChange[] = [];
  // `out` keeps unresolved commands for the fill; `check` keeps only their
  // new value, for the value check.
  let out = "";
  let check = "";
  let last = 0;
  const replaceIn = (earlier: string, from: string, to: string): string => {
    const hit = findLast(earlier, from);
    if (!hit) return earlier;
    const old = earlier.slice(hit.index, hit.index + hit.length);
    // A number given for a labelled value replaces only the number.
    const replacement =
      new RegExp(`^${NUMBER}$`).test(to) && lastNumber.test(old)
        ? old.replace(lastNumber, to)
        : to;
    return `${earlier.slice(0, hit.index)}${replacement}${earlier.slice(hit.index + hit.length)}`;
  };
  for (const match of text.matchAll(CHANGE)) {
    const [whole, lead = "", fromRaw = "", toRaw = ""] = match;
    const from = fromRaw.trim();
    const to = toRaw.trim();
    const start = (match.index ?? 0) + lead.length;
    const end = (match.index ?? 0) + whole.length;
    const between = text.slice(last, start);
    const earlier = out + between;
    if (from && to && findLast(earlier, from)) {
      out = replaceIn(earlier, from, to);
      check = replaceIn(check + between, from, to);
      applied++;
    } else {
      // Not in this dictation: the fill changes it in the report.
      out = earlier + whole.slice(lead.length);
      check = `${check}${between} ${to}.`;
      if (from && to) pending.push({ from, to });
    }
    last = end;
  }
  const rest = text.slice(last);
  out += rest;
  const checkText = check + rest;
  const tidy = (value: string): string =>
    value
      .replace(/[ \t]{2,}/g, " ")
      .replace(/\s+([,.;:])/g, "$1")
      .replace(/([.;:]),/g, "$1")
      .replace(/^[\s,.;:]+/, "")
      .trim();
  return { text: tidy(out), applied, pending, checkText: tidy(checkText) };
}
